"use client";
import React, { useState, useEffect } from "react";
import styles from "./ShortBar.module.scss";
import SvgEndChat from "@/app/icons/SvgEndChat.svg";
import { Button, Modal } from "@douyinfe/semi-ui";
import { useMessageStore } from "@/app/store";
import {
  CurrentChatStatus,
  MessageSender,
  MessageType,
  ScrollToBottom,
  SocketConnectionStatus,
} from "@/app/store/types";
import { webSocketService } from "@/app/services/WebSocketService";
import SemiButton from "@/app/ui/button";
import NewMessageTips from "./modules/NewMessageTips";
import { closeConversation, reportPush } from "@/app/api/message";
import Locale from "@/app/locales";
import { ReportScene, reportScene } from "@/app/services/DataReporter";

interface ShortBarProps {
  handleScrollToBottom: ScrollToBottom;
  pageVisible?: boolean;
}

export default function ShortBar(props: ShortBarProps) {
  const { conversationId, chatStatus, isConnected, setConnectionStatus } =
    useMessageStore();
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 检测是否为移动端
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const handleOk = async () => {
    if (conversationId) {
      setConnectionStatus(SocketConnectionStatus.DISCONNECTED);
      reportScene(ReportScene.EXIT_CHAT);
      const [_, pushSatisfy] = await Promise.all([
        closeConversation(),
        reportPush(conversationId),
      ]);
      // 如果没有推送过，就再推送一条
      webSocketService.sendMessage(
        JSON.stringify({ text: Locale.systemMsg.chatEnd }),
        {
          senderType: MessageSender.USER,
          msgType: MessageType.Template,
          noNeedSendToWebsocket: true,
        },
      );
      if (pushSatisfy) {
        webSocketService.sendMessage("", {
          senderType: MessageSender.USER,
          msgType: MessageType.Satisfy,
          noNeedSendToWebsocket: true,
        });
      }
      // 主动关闭会话
      setVisible(false);
    }
  };
  const handleCancel = () => {
    setVisible(false);
  };

  // 当用户在线，并且不是排队中的场景时，显示结束会话按钮
  if (
    isConnected !== SocketConnectionStatus.CONNECTED ||
    chatStatus === CurrentChatStatus.LineUp
  ) {
    return null;
  }

  return (
    <div className={styles.shortBarContainer}>
      {/* PC端的结束会话按钮已移动到ChatInput组件中 */}
      {isMobile && (
        <div className={styles.left}>
          <Button
            theme="borderless"
            onClick={() => setVisible(true)}
            type="tertiary"
            className={styles.endButton}
          >
            <SvgEndChat />
            {Locale.button.endMessage}
          </Button>
        </div>
      )}
      <div className={styles.right}>
        <NewMessageTips {...props} />
      </div>
      {/* 移动端的结束会话确认弹窗 */}
      {isMobile && (
        <Modal
          title={Locale.chatBar.tips}
          visible={visible}
          onCancel={handleCancel}
          closeOnEsc={true}
          size="small"
          width={300}
          centered
          footer={
            <SemiButton onClick={handleOk}>{Locale.button.confirm}</SemiButton>
          }
        >
          {Locale.chatBar.confirmEndMessage}
        </Modal>
      )}
    </div>
  );
}
